// @flow
import EventEmitter from 'eventemitter3';
import LegacyDispatcher from 'app/lib/legacyDispatcher';
import Database from 'app/lib/Database';
import string from 'app/lib/string';

type jQueryAjax = {};

type jQueryPromise = {
  done: (callback: (result: Array<Object>) => mixed) => jQueryPromise
};

declare var jQuery: {
  ajax: (options: {
    url: string,
    dataType?: string,
    method: string,
    data: mixed,
    success: () => mixed,
    error: () => mixed
  }) => jQueryAjax,
  when: (ajax: jQueryAjax) => jQueryPromise
};

declare var _: {
  extend: () => mixed,
  wthout: () => mixed
};

type Project = {
  id: string
};

type Settings = {};

class UserStore {
  pipelineSettings: Settings;
  eventEmitter: EventEmitter;

  constructor() {
    this.pipelineSettings = {};
    this.eventEmitter = new EventEmitter();

    this.registerWithDispatcher();
  }

  emitChange() {
    return this.eventEmitter.emit('change');
  }

  updateProjectSettings(project: Project, settings: Settings) {
    const data = { "_method": "put" };

    // Convert the keys to underscore case
    Object.entries(settings).forEach(([key, value]) => {
      // Uber hack so jQuery will send an empty array. We need these keys to be
      // present in the JSON payload that we send, and if we send an empty array
      // to jQuery, it'll drop it from post.
      if (Array.isArray(value) && value.length === 0) {
        value = [""];
      }

      data["pipeline_settings[" + string.underscore(key) + "]"] = value;
    });

    const ajax = jQuery.ajax({
      url: "/user/pipeline_settings/" + project.id.toString(),
      dataType: "json",
      method: "POST",
      data: data,
      success: function() {
        LegacyDispatcher.emit("pipeline_settings:update:success", {
          project: { id: project.id },
          settings: settings
        });
      },
      error: function() {
        LegacyDispatcher.emit("pipeline_settings:update:error", {
          project: { id: project.id },
          settings: settings
        });
      }
    });

    // Return a promise
    return jQuery.when(ajax);
  }

  starProjectBranch(project: Project, branch: string) {
    const ajax = jQuery.ajax({
      url: "/user/pipeline_settings/" + project.id.toString() + "/star",
      method: "POST",
      data: { "branch": branch },
      success: function() {
        LegacyDispatcher.emit("pipeline_settings:update:success", {
          project: { id: project.id }
        });
      },
      error: function() {
        LegacyDispatcher.emit("pipeline_settings:update:error", {
          project: { id: project.id }
        });
      }
    });

    // Return a promise
    return jQuery.when(ajax);
  }

  unstarProjectBranch(project: Project, branch: string) {
    const ajax = jQuery.ajax({
      url: "/user/pipeline_settings/" + project.id.toString() + "/unstar",
      method: "POST",
      data: { "branch": branch },
      success: function() {
        LegacyDispatcher.emit("pipeline_settings:update:success", {
          project: { id: project.id }
        });
      },
      error: function() {
        LegacyDispatcher.emit("pipeline_settings:update:error", {
          project: { id: project.id }
        });
      }
    });

    // Return a promise
    return jQuery.when(ajax);
  }

  registerWithDispatcher() {
    let existingSettings;
    LegacyDispatcher.on('pipeline_settings:update', ({ project, settings }) => {
      existingSettings = this.pipelineSettings[project.id];
      const updatedSettings = (this.pipelineSettings[project.id] = Object.assign(existingSettings, settings));

      this.updateProjectSettings(project, updatedSettings);

      this.emitChange();
    });

    LegacyDispatcher.on('pipeline_settings:update:success', () => this.emitChange());

    LegacyDispatcher.on('pipeline_settings:update:error', () => this.emitChange());

    LegacyDispatcher.on('project_branch:star', ({ project, branch }) => {
      existingSettings = this.pipelineSettings[project.id];
      existingSettings.starredBranches.push(branch);
      existingSettings.unstarredBranches = existingSettings.unstarredBranches.filter((unstarredBranch) => unstarredBranch !== branch);

      this.starProjectBranch(project, branch);

      this.emitChange();
    });

    LegacyDispatcher.on('project_branch:unstar', ({ project, branch }) => {
      existingSettings = this.pipelineSettings[project.id];
      existingSettings.starredBranches = existingSettings.starredBranches.filter((starredBranch) => starredBranch !== branch);
      existingSettings.unstarredBranches.push(branch);

      this.unstarProjectBranch(project, branch);

      this.emitChange();
    });
  }

  bootstrap(project: Project, settings: Settings) {
    return this.pipelineSettings[project.id] = Database.parse(settings);
  }

  addChangeListener(callback: Function) {
    return this.eventEmitter.addListener('change', callback);
  }

  removeChangeListener(callback: Function) {
    return this.eventEmitter.removeListener('change', callback);
  }

  getEmailSettings(project: Project) {
    return this.pipelineSettings[project.id];
  }

  getStarredBranches(project: Project) {
    let settings;

    if ((settings = this.pipelineSettings[project.id])) {
      return settings.starredBranches;
    }

    return [];
  }
}

export default new UserStore();
